import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { FC, ReactNode, useState } from 'react';
import FeelingAnxious from './FeelingAnxious';
import HelpingAbout from './HelpingAbout';
import HurtingMyself from './HurtingMyself';
import SelfCare from './SelfCare';
import WhatExpect from './WhatExpect';

interface TabPanelProps {
  children?: ReactNode;
  dir?: string;
  index: any;
  value: any;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`wrapped-tabpanel-${index}`} aria-labelledby={`wrapped-tab-${index}`} {...other}>
      {value === index && (
        <Box
          sx={{
            padding: 3
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: any) {
  return {
    id: `wrapped-tab-${index}`,
    'aria-controls': `wrapped-tabpanel-${index}`
  };
}

const HotlineTabpanel: FC<{ webchat_language: string }> = ({ webchat_language = 'en' }) => {
  const [value, setValue] = useState(0);

  const handleChange = (_event: unknown, newValue: number) => {
    setValue(newValue);
  };

  const tab1_es = (
    <>
      <p>
        No siempre es fácil saber qué decir cuando alguien nos dice que ha sido abusado sexualmente, especialmente cuando dicha persona es un miembro de la familia, un amigo(a) o un ser querido.
        Considere las siguientes formas de mostrar su apoyo:
      </p>
      <li>Escuche. Esté presente. Comuníquese sin juzgar.</li>
      <li>Si el sobreviviente busca atención médica o planea denunciar, ofrezca estar ahí. Su presencia puede brindar el apoyo que él o ella necesita.</li>
      <li>
        Motive al sobreviviente a obtener ayuda. Comparta recursos como la Línea de Ayuda Nacional del Asalto Sexual online.rainn.org, pero sea consciente de que solo él o ella puede tomar la decisión
        de obtener ayuda.
      </li>
      <li>Sea paciente. Recuerde, no existe un plazo para recuperarse del trauma. Evite ponerle presión para involucrarse en actividades que aún no está preparado(a) para realizar.</li>
      <li>Motívela(o) a tomar buen cuidado de sí misma(o) durante estos momentos difíciles.</li>
      <li>
        La ideación suicida puede ser común entre las personas que han vivido un trauma sexual. Si alguien importante en su vida está considerando suicidarse, aprenda sobre las señales de advertencia
        y ofrézcale ayuda y apoyo. Para obtener más información sobre la prevención del suicidio, visite National Suicide Prevention Lifeline o llame al 800.273.TALK (8255) a cualquier hora, día o
        noche.{' '}
      </li>
      <li>Para buscar su proveedor local de servicios para víctimas del asalto sexual, haga clic aquí.</li>
      <p>
        Es importante que usted también tome cuidado de sí mismo(a). Usted puede llamar la Línea de Ayuda Nacional del Asalto Sexual al 800.656.HOPE (4673) o visite online.rainn.org y reciba apoyo
        confidencial.
      </p>
    </>
  );

  const tab2_es = (
    <>
      <p>
        El cuidado de sí mismo(a) se trata de los pasos a tomar para sentirse saludable y cómodo. Bien sea que haya sucedido recientemente o hace años, el cuidado de sí mismo(a) le puede ayudar a
        sobrellevar los efectos a corto y largo plazo de un trauma como lo es el asalto sexual.
      </p>
      <p>
        <strong>Su cuidado físico</strong>
      </p>
      <p>
        Después de un trauma, es importante mantener su cuerpo fuerte y saludable. Usted podría estar recuperándose de lesiones o podría estar sintiéndose emocionalmente agotado. La buena salud física
        lo puede sostener por estos tiempos. Piense en una época en la que usted se sentía físicamente saludable y considere hacerse las siguientes preguntas:
      </p>
      <li>¿Cómo estaba durmiendo? ¿Tenía usted una costumbre para dormirse o tomaba siestas para sentirse más descansada(o)?</li>
      <li>¿Qué tipos de alimentos estaba consumiendo? ¿Qué alimentos lo(a) hacían sentirse fuerte y saludable?</li>
      <li>¿Qué tipos de ejercicios disfrutaba? ¿Habían ciertas actividades que la(o) hacían sentirse con más energía?</li>
      <li>¿Llevaba a cabo ciertas rutinas? ¿Habían actividades que realizaba para empezar bien el día o para relajarse al final del mismo?</li>
      <p>
        <strong>El cuidado emocional de sí mismo</strong>
      </p>
      <p>
        El cuidado emocional de sí mismo(a) tiene distintos significados para distintas personas. La clave del cuidado emocional de sí mismo(a) es de estar en armonía consigo mismo. Piense en una
        época en la que se sentía bien equilibrado(a) y con los pies sobre la tierra, y considere hacerse las siguientes preguntas:
      </p>
      <li>¿Qué actividades divertidas o pasatiempos disfrutaba? ¿Habían eventos que disfrutaba o paseos que anhelaba realizar?</li>
      <li>¿Usted escribía sus pensamientos en un diario o en un cuaderno personal?</li>
      <li>¿Formaban parte las actividades de meditación o relajación de su horario normal?</li>
      <li>
        ¿Qué palabras inspiradoras estaba leyendo? ¿Tenía un autor en particular o un sitio web favorito, como el{' '}
        <strong>
          <a href="https://www.pinterest.com/rainn01/healthy-happy-safe/">Pintrest board</a>
        </strong>{' '}
        de RAINN al cual ir para obtener inspiración?
      </li>
      <li>¿Con quiénes le gustaba estar? ¿Había alguien o un grupo de personas con las que se sentía seguro(a) y respaldado?</li>
      <li>¿En dónde pasaba tiempo? ¿Había un lugar especial, quizás al aire libre o en casa de algún(a) amigo(a) en donde se sentía cómoda(o) y con los pies sobre la tierra?</li>
      <p>
        El cuidado de sí mismo no siempre es fácil de tomar por cuenta propia. Para hablar con alguien que está capacitado para ayudarle, llame la Línea de Ayuda Nacional del Asalto Sexual al
        800.656.HOPE(4673) o para conversar online: online.rainn.org.
      </p>
    </>
  );

  const tab3_es = (
    <>
      <p>
        Para nadie es fácil aprender a procesar sus sentimientos después de un asalto sexual, y es común que pasen por su mente pensamientos sobre cómo hacerse daño. Aunque tener estos pensamientos
        puede hacerla(o) sentir aislada(o), usted no tiene que lidiar con ellos sola(o). Hay ayuda disponible.
      </p>
      <p>
        <strong>¿Por qué me siento así?</strong>
      </p>
      <p>
        Usted no está sola(o). El hacerse daño a sí mismo y el suicidio se pueden evitar y estos pensamientos no son permanentes. Si usted está pensando hacerse daño, existen recursos que le darán el
        apoyo que necesita para pasar por este período tan difícil. Recuerde, usted es fuerte y hay ayuda disponible.
      </p>
      <p>
        Es importante saber que hay muchas personas que en algún momento de sus vidas han pensado hacerse daño o cometer suicidio.Si usted está teniendo estos pensamientos, quizás significa que usted
        está pasando por una crisis y necesita apoyo.
      </p>
      <p>
        <strong>¿En dónde puedo encontrar ayuda y aprender más?</strong>
      </p>
      <li>
        La Red Nacional de Prevención del Suicidio. Llame la Red al 800.273.TALK (8255), o converse online por medio de Lifeline Crisis Chat (Red para conversar en momentos de crisis). La Red no es un
        servicio anónimo y parte de la información personal, como la dirección IP o el número telefónico, podría ser recolectada y utilizada si la persona que llama corre peligro. Si a usted le
        preocupa la privacidad, repase las normas de la organización antes de usar cualquiera de estos servicios.
      </li>
      <li>
        Línea de Crisis para Veteranos. Si usted es un veterano, puede llamar la Línea de Crisis para Veteranos al 800.273.825 y oprima 1. También puede visitar su online chat service (servicio de
        conversación online) para recibir apoyo las 24 horas del día, 7 días por semana.
      </li>
    </>
  );

  const tab4_es = (
    <>
      <p>
        Es normal sentirse ansioso(a), molesto(a), temeroso(a) o a veces incómodo(a). Además es normal que estos sentimientos se intensifiquen después de un trauma como el asalto sexual. A
        continuación hay algunas sugerencias sobre lo que puede hacer si se está sintiendo agobiado(a.)
      </p>
      <p>
        <strong>Lo que le puede ayudar cuando se sienta agobiado(a) o asustado(a)</strong>
      </p>
      <li>Dígase a sí misma(o) que está segura(o). Acuérdese de que el evento en sí, ya pasó y que usted sobrevivió.</li>
      <li>Respire.</li>
      <ul>Respire profundo y lentamente, colocando su mano sobre el estómago. Deberá ver su mano apartarse con las inhalaciones y la verá bajar al soltar la respiración.</ul>
      <ul>
        Cuando nos asustamos, nuestro cuerpo empieza a tomar respiraciones cortas y leves, y la disminución del oxígeno nos puede hacer sentir más asustados. La respiración profunda es importante
        porque aumenta la cantidad de oxígeno en nuestro Sistema y nos ayuda a salir más rápidamente del estado ansioso.
      </ul>
      <li>Regrese al presente usando los cinco sentidos.</li>
      <ul>La vista: Mire a su alrededor. Haga una lista de los artículos en el cuarto; cuente los colores o los muebles a su alrededor. ¿Qué ve?</ul>
      <ul>El olfato: Respire un aroma reconfortante, o enfóquese en los olores alrededor suyo. ¿Qué olores percibe?</ul>
      <ul>El oído: Escuche los sonidos a su alrededor, o encienda la música. ¿Qué escucha?</ul>
      <ul>El gusto: Coma o beba algo que disfruta. Enfóquese en el sabor ¿A qué le sabe?</ul>
      <ul>El tacto: Sostenga algo frío, como un pedazo de hielo, o caliente, como una taza de té. ¿Cómo se siente?</ul>
      <li>Reconozca lo que la(o) hace sentir más segura(o).</li>
      <ul>Abríguese con una manta o vaya solo(a) a un cuarto y cierre la puerta. Haga lo que sea necesario para que se sienta seguro(a).</ul>
      <li>Llame a alguien si necesita apoyo. Usted no tiene que pasar por esto sola(o). Si usted tiene un(a) amigo(a) o un familiar en quien confía, llámela(o.)</li>
      <p>
        Usted ha tomado la decisión correcta viniendo a la Línea de Ayuda Online—alguien le espera para ayudarle. Si usted desea hablar con alguien inmediatamente, siempre puede llamar la Línea de
        Ayuda Nacional del Asalto Sexual al 800.656.HOPE (4673) para hablar con alguien capacitado en su comunidad para ayudarle.
      </p>
    </>
  );

  const tab5_es = (
    <>
      <p>
        <strong>¿Cómo es el consentimiento?</strong>
      </p>
      <p>
        Las leyes sobre el consentimiento varían por estado y por situación. Puede hacer que el tema sea confuso, pero no tiene que ser un experto legal para entender cómo se da el consentimiento en
        la vida real.
      </p>
      <p>
        <strong>¿Qué es el consentimiento?</strong>
      </p>
      <p>
        El consentimiento es un acuerdo entre los participantes a tener relaciones sexuales. Existen muchas maneras de dar consentimiento y abajo se tratan algunas de ellas. El consentimiento no tiene
        que ser verbal, pero el acordar verbalmente a tener diferentes actividades sexuales le puede ayudar tanto a usted como a su compañero(a) a respetar los límites de cada uno.
      </p>
      <p>
        <strong>¿Cómo se da el consentimiento en la vida real?</strong>
      </p>
      <p>
        Cuando usted está entablando una relación sexual, <strong>el consentimiento se trata de la comunicación</strong>. Y se debe tener cada vez. El dar consentimiento para una actividad, una vez,
        no significa que esté dando consentimiento para mayor contacto sexual o recurrente. Por ejemplo, estar de acuerdo en besar a alguien no le da a esa persona permiso de quitarle su ropa. El
        haber tenido una relación sexual con alguien en el pasado no le da permiso a esa persona de tener relaciones sexuales con usted nuevamente en el futuro.
      </p>
      <p>
        <strong>Usted puede cambiar de parecer en cualquier momento.</strong>{' '}
      </p>
      <p>
        Usted puede retirar su consentimiento en cualquier momento cuando se sienta incómodo(a). Es importante comunicarle a su compañero(a) con claridad que usted ya no se siente cómodo(a) con esta
        actividad y desea parar. La mejor forma de asegurar que ambas partes se sienten cómodas con cualquier actividad sexual es hablando al respecto.
      </p>
      <p>
        <strong>El consentimiento positivo es algo parecido a esto:</strong>
      </p>
      <ul>Comunicando cuando usted cambia el tipo o el nivel de actividad sexual con frases como, "¿te parece bien esto?"</ul>
      <ul>Estar de acuerdo explícitamente en ciertas actividades, ya sea diciendo "sí" o con otra declaración afirmativa cómo "estoy dispuesta(o) a intentarlo".</ul>
      <ul>Empleando señales físicas para hacerle saber a la otra persona que usted se siente cómoda(o) llevando las cosas al siguiente nivel.</ul>
      <p>
        <strong>NO es algo parecido a esto:</strong>
      </p>
      <ul>Rehusar entender el "no"</ul>
      <ul>Asumiendo que el llevar cierta ropa, el coqueteo o el besar sea una invitación para algo más</ul>
      <ul>Alguien que sea menor de la edad legal de consentimiento sexual, como lo define el estado</ul>
      <ul>Alguien incapacitado por drogas o por alcohol</ul>
      <ul>El presionar a alguien a tener actividades sexuales mediante el temor o la intimidación</ul>
      <ul>Asumir que usted tiene permiso de entablar un acto sexual porque lo ha hecho en el pasado</ul>
      <p>Si usted ha vivido el asalto sexual, usted no está solo(a). Llame a la Línea de Ayuda Nacional del Asalto Sexual al 800.656.4673, o converse online en rainn.org/es.</p>
      <p>
        <strong>DESCARGO DE RESPONSABILIDAD LEGAL</strong>
      </p>
      <li>
        El sitio web de la Red nacional contra la violación, el abuso y el incesto (RAINN) brinda información generalque tiene la intención de ser correcta y estar actualizada, mas no se garantiza que
        así sea. La información no se presenta como fuente de asesoría legal. Usted no debe depender de las declaraciones ni de las representaciones dadas en este sitio ni en ninguno de los sitios de
        internet mencionados externamente para fines de asesoría legal. Si usted requiere de asesoría legal sobre en la que puede confiar en la trayectoria de sus asuntos legales, consulte a un
        abogado independiente y competente. RAINN no asume la responsabilidad por las acciones o falta de las mismas que tengan las personas que han visitado este sitio, y nadie tendrá derecho a una
        demanda por depender de forma perjudicial de cualquier tipo de información.
      </li>
    </>
  );

  return (
    <div className="hotline-tabpanel">
      <AppBar position="static" color="default">
        <Tabs value={value} onChange={handleChange} indicatorColor="primary" textColor="primary" aria-label="full width tabs example" variant="scrollable" scrollButtons="auto">
          <Tab wrapped label={webchat_language === 'en' ? 'What can I Expect on the Hotline?' : 'Ayúdele a Ese Ser Querido'} {...a11yProps(0)} />
          <Tab wrapped label={webchat_language === 'en' ? 'Self Care After Trauma' : 'Cuidar de sí Misma(o) Después del Trauma'} {...a11yProps(1)} />
          <Tab wrapped label={webchat_language === 'en' ? 'I am Thinking about Hurting Myself' : 'Estoy Pensando en Hacerme Daño'} {...a11yProps(2)} />
          <Tab wrapped label={webchat_language === 'en' ? "I'm Feeling Anxious or Upset" : 'Me Siento Ansiosa(o) o Molesta(o)'} {...a11yProps(3)} />
          <Tab wrapped label={webchat_language === 'en' ? 'Helping Someone You Care About' : '¿Qué es el consentimiento?'} {...a11yProps(4)} />
        </Tabs>
      </AppBar>

      <TabPanel value={value} index={0}>
        {webchat_language === 'en' ? <WhatExpect /> : tab1_es}
      </TabPanel>
      <TabPanel value={value} index={1}>
        {webchat_language === 'en' ? <SelfCare /> : tab2_es}
      </TabPanel>
      <TabPanel value={value} index={2}>
        {webchat_language === 'en' ? <HurtingMyself /> : tab3_es}
      </TabPanel>
      <TabPanel value={value} index={3}>
        {webchat_language === 'en' ? <FeelingAnxious /> : tab4_es}
      </TabPanel>
      <TabPanel value={value} index={4}>
        {webchat_language === 'en' ? <HelpingAbout /> : tab5_es}
      </TabPanel>
    </div>
  );
};

export default HotlineTabpanel;
